import axios from "axios";
import { ConfigCatFeatureFlag } from "util/CommonConstant";
import { getEnvVariableValue, getLocalStorage } from "util/CommonUtil";
import HelperService from "./HelperService";

type HTTP_METHODS = "get" | "post" | "put" | "delete" | "patch";

class BaseApiService {
  //Properties
  protected defaultLimit = 25;

  getDefaultApiUrl(enabledOverride?: boolean) {
    const isChangeAPIURLEnabled = HelperService?.getConfigCatFlag(
      ConfigCatFeatureFlag.CHANGE_API_URL
    );
    if (isChangeAPIURLEnabled || enabledOverride) {
      const overriddenUrl = getLocalStorage("BASE_API_URL_OVERRIDDEN");
      if (overriddenUrl) {
        return overriddenUrl;
      }
    }
    return getEnvVariableValue("VITE_BASE_API_URL");
  }

  getAxios() {
    return axios;
  }

  makeGetRequestWithAuth(url: string, headers?: any, responseType?: any) {
    url = `${this.getDefaultApiUrl()}${url}`;
    const headersObj = {
      headers: { ...HelperService.getHeadersWithAuth(), ...(headers || {}) },
      ...(responseType ? { responseType: responseType } : {}),
    };
    return this.getAxios().get(url, headersObj);
  }

  makeGetRequest(url: string, headers?: any) {
    url = `${this.getDefaultApiUrl()}${url}`;
    const headersObj = {
      headers: { ...HelperService.getHeaders(), ...(headers || {}) },
    };
    return this.getAxios().get(url, headersObj);
  }

  makePostRequestWithAuth(url: string, body?: any, headers?: any, responseType?: any) {
    url = `${this.getDefaultApiUrl()}${url}`;
    const headersObj = {
      headers: { ...HelperService.getHeadersWithAuth(), ...(headers || {}) },
      ...(responseType ? { responseType: responseType } : {}),
    };
    return this.getAxios().post(url, body, headersObj);
  }

  makePostRequestWithAuthForMultiPartFormData(url: string, body?: any, headers?: any) {
    url = `${this.getDefaultApiUrl()}${url}`;
    const headersObj = {
      headers: { ...HelperService.getHeadersWithAuthWithMultipartFormData(), ...(headers || {}) },
    };
    return this.getAxios().post(url, body, headersObj);
  }

  makePostRequestWithAuthWithoutObject(url: string, body?: any, headers?: any) {
    url = `${this.getDefaultApiUrl()}${url}`;
    const headersObj = {
      headers: { ...HelperService.getHeadersWithAuth(), ...(headers || {}) },
    };
    return this.getAxios().post(url, body, headersObj);
  }

  makePostRequest(url: string, body?: any, headers?: any) {
    url = `${this.getDefaultApiUrl()}${url}`;
    const headersObj = {
      headers: { ...HelperService.getHeaders(), ...(headers || {}) },
    };
    return this.getAxios().post(url, body, headersObj);
  }

  makePutRequestWithAuth(url: string, body?: any, headers?: any) {
    url = `${this.getDefaultApiUrl()}${url}`;
    const headersObj = {
      headers: { ...HelperService.getHeadersWithAuth(), ...(headers || {}) },
    };
    return this.getAxios().put(url, body, headersObj);
  }
  makePatchRequestWithAuth(url: string, body?: any, headers?: any) {
    url = `${this.getDefaultApiUrl()}${url}`;
    const headersObj = {
      headers: { ...HelperService.getHeadersWithAuth(), ...(headers || {}) },
    };
    return this.getAxios().patch(url, body, headersObj);
  }
  makePutRequest(url: string, body?: any, headers?: any) {
    url = `${this.getDefaultApiUrl()}${url}`;
    const headersObj = {
      headers: { ...HelperService.getHeaders(), ...(headers || {}) },
    };
    return this.getAxios().put(url, body, headersObj);
  }

  makeDeleteRequestWithAuth(url: string, headers?: any) {
    url = `${this.getDefaultApiUrl()}${url}`;
    const headersObj = {
      headers: { ...HelperService.getHeadersWithAuth(), ...(headers || {}) },
    };
    return this.getAxios().delete(url, headersObj);
  }
  makeDeleteRequest(url: string, headers?: any) {
    url = `${this.getDefaultApiUrl()}${url}`;
    const headersObj = {
      headers: { ...HelperService.getHeaders(), ...(headers || {}) },
    };
    return this.getAxios().delete(url, headersObj);
  }

  axiosConfig(
    url: string,
    method: HTTP_METHODS,
    headers: any = HelperService.getHeaders() ?? undefined,
    data?: any,
    timeout?: number
  ) {
    const config = {
      url: `${this.getDefaultApiUrl()}${url}`,
      method,
      headers: {
        ...headers,
      },
      data: data,
      timeout: timeout,
    };
    return axios(config);
  }
  axiosConfigWithAuth(
    url: string,
    method: HTTP_METHODS,
    headers?: any,
    data?: any,
    timeout?: number
  ) {
    let config = {};
    config = {
      url: `${this.getDefaultApiUrl()}${url}`,
      method,
      headers: { ...HelperService.getHeadersWithAuth(), ...(headers || {}) },
      data: data,
      timeout: timeout,
    };
    return axios(config);
  }

  cancelApiWithToken(token: string) {
    const url = `${this.getDefaultApiUrl()}/cancel-token/{${token}}`;
    return this.getAxios().post(url);
  }
}

export default BaseApiService;
